"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { AuthUser } from "@podlog/shared";
import { fetchMe, logout } from "../lib/api";

interface AuthState {
  user: AuthUser | null;
  loading: boolean;
  /** Unread-notification count seeded from /api/auth/me on load, so the header
   *  bell has an initial badge without its own request. */
  initialUnread: number;
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [initialUnread, setInitialUnread] = useState(0);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const me = await fetchMe();
    setUser(me.user);
    setInitialUnread(me.unread_count);
  }, []);

  useEffect(() => {
    void (async () => {
      if (typeof window !== "undefined" && window.location.hash.startsWith("#token=")) {
        const token = window.location.hash.substring(7);
        const { setAuthToken } = await import("../lib/api");
        setAuthToken(token);
        window.history.replaceState(null, "", window.location.pathname + window.location.search);
      }
      const me = await fetchMe();
      setUser(me.user);
      setInitialUnread(me.unread_count);
      setLoading(false);
    })();
  }, []);

  const signOut = useCallback(async () => {
    await logout();
    setUser(null);
    setInitialUnread(0);
  }, []);

  const value = useMemo(
    () => ({ user, loading, initialUnread, refresh, signOut }),
    [user, loading, initialUnread, refresh, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider.");
  return ctx;
}
